import React, { FC, memo, useState } from 'react';

import {
  NOTIFICATION_DEFAULT_DELAY,
  NOTIFICATION_DEFAULT_ERROR_MESSAGE,
  useNotificationContext,
} from 'application/providers/NotificationProvider';
import { useCompanyId } from 'application/providers/AccountProvider';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';

import {
  CompanyAdCampaignsEarlyReportMutation,
  useMediaCompanyAdCampaignsEarlyReportMutation,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import DateRangeInput from '@zvuk-b2b/react-uikit/ui/DateInput/DateRangeInput';
import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import { TEXTS } from './texts';
import { FIRST_REFETCH_INTERVAL, MAX_DATE, MIN_DATE } from './consts';
import { validateDateRange } from './utils';

import styles from './index.module.scss';

const RequestAdCampaignsReportModal: FC<ModalProps[ModalTypes.REPORT_CREATE_AD_CAMPAIGNS]> = ({
  refetchReports,
  close,
}) => {
  const notification = useNotificationContext();
  const { isMobile } = useMedia();

  const companyId = useCompanyId();

  const [mutation, { loading }] = useMediaCompanyAdCampaignsEarlyReportMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const onDateRangeChange = (startDate: Date | null, endDate: Date | null) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const onRequestSubmit = async () => {
    try {
      if (startDate === null || endDate === null) return;

      const validationError = validateDateRange(startDate, endDate);

      if (validationError) {
        notification.showErrorNotification({
          children: validationError,
          delay: NOTIFICATION_DEFAULT_DELAY,
        });

        return;
      }

      const result = await mutation({
        variables: {
          input: {
            companyId,
            dateRange: {
              dateFrom: DateUtils.getStartOfDate(startDate).toISOString(),
              dateTo: DateUtils.getEndOfDate(endDate).toISOString(),
            },
          },
        },
      });

      const reportResult = result.data?.result as CompanyAdCampaignsEarlyReportMutation | null | undefined;

      if (!reportResult || !reportResult.ok) {
        throw new Error();
      }

      if (!reportResult.isCreated) {
        notification.showInfoNotification({
          children: TEXTS.REQUEST_REPORT_EXISTS_NOTIFICATION,
          delay: NOTIFICATION_DEFAULT_DELAY * 3,
        });

        return;
      }

      close();

      notification.showInfoNotification({
        children: TEXTS.REQUEST_REPORT_SUCCESS_NOTIFICATION,
        delay: NOTIFICATION_DEFAULT_DELAY * 3,
      });

      setTimeout(() => {
        refetchReports();
      }, FIRST_REFETCH_INTERVAL);
    } catch (e) {
      const error = e as Error;

      if (/has not access to AD reports/.test(error.message)) {
        notification.showErrorNotification({
          children: TEXTS.REQUEST_REPORT_ERROR_NO_ADS_NOTIFICATION,
        });
      } else {
        notification.showErrorNotification({
          children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
        });
      }
    }
  };

  return (
    <ModalScreen
      title={TEXTS.REQUEST_REPORT_MODAL_TITLE}
      onClose={close}
      actionButtons={[
        <Button
          elementId="button_submit_request_ad_campaigns_report_modal"
          key="button_submit_request_ad_campaigns_report_modal"
          type={ButtonType.PRIMARY}
          onClick={onRequestSubmit}
          isLoading={loading}
          isDisabled={startDate === null || endDate === null}>
          {TEXTS.REQUEST}
        </Button>,
        <Button
          elementId="button_cancel_request_ad_campaign_report_modal"
          key="button_cancel_request_ad_campaign_report_modal"
          type={ButtonType.SECONDARY}
          onClick={() => close()}>
          {TEXTS.CLOSE}
        </Button>,
      ]}>
      <div className={styles.content}>{TEXTS.REQUEST_REPORT_MODAL_CONTENT}</div>
      <DateRangeInput
        elementId="input_ad_campaigns_report_date_range"
        startDate={startDate}
        endDate={endDate}
        onChange={onDateRangeChange}
        popperPlacement="right"
        minDate={MIN_DATE}
        maxDate={MAX_DATE}
        label={TEXTS.LABEL}
        containerClassName={styles.dateRangeInput}
        showPreviousMonths={false}
        openToDate={isMobile ? MIN_DATE : startDate || new Date()}
        monthsShown={isMobile ? 12 : 1}
        focusSelectedMonth={!isMobile}
        isClearable
        withPortal={isMobile}
      />
      <div className={styles.footer}>{TEXTS.HINT}</div>
    </ModalScreen>
  );
};

export default memo(RequestAdCampaignsReportModal);
